import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createGraphEdge, createGraphNode } from "../../scenes/graphPrimitives";
import { createTextSprite, updateTextSprite } from "../../scenes/shared/textSpritePrimitives";
import { buildTreeById, createBinaryTreeLayout } from "../../scenes/shared/treeUtils";

const DEFAULT_TREE_INPUT = {
    rootId: 1,
    nodes: [
        { id: 1, value: -10, leftId: 2, rightId: 3 },
        { id: 2, value: 9, leftId: null, rightId: null },
        { id: 3, value: 20, leftId: 4, rightId: 5 },
        { id: 4, value: 15, leftId: null, rightId: null },
        { id: 5, value: 7, leftId: null, rightId: null },
    ],
};

const GRAPH_SCALE = 1.02;
const GRAPH_OFFSET = new THREE.Vector3(1.15, 0, 0);

const BACKDROP_Z = -2.25;
const LANE_Z = -0.95;
const LANE_HEIGHT = 0.62;

const NODE_IDLE_COLOR = new THREE.Color(0x334155);
const NODE_ACTIVE_COLOR = new THREE.Color(0xe0f2fe);
const NODE_NEGATIVE_RETURN_COLOR = new THREE.Color(0xdc2626);
const NODE_ZERO_RETURN_COLOR = new THREE.Color(0x475569);
const NODE_POSITIVE_RETURN_COLOR = new THREE.Color(0x14b8a6);
const NODE_BEST_COLOR = new THREE.Color(0xd946ef);

const EDGE_IDLE_COLOR = new THREE.Color(0x334155);
const EDGE_ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const EDGE_RESOLVED_COLOR = new THREE.Color(0x22c55e);

function edgeKey(fromId, toId) {
    return `${fromId}:${toId}`;
}

function normalizeTreeInput(input) {
    const source = input || DEFAULT_TREE_INPUT;

    return {
        rootId: source.rootId,
        nodes: source.nodes.map((node) => ({
            id: node.id,
            value: node.value,
            leftId: node.leftId,
            rightId: node.rightId,
        })),
    };
}

function buildMaxPathSteps(treeInput) {
    const treeById = buildTreeById(treeInput.nodes);
    const steps = [
        {
            type: "start",
            rootId: treeInput.rootId,
        },
    ];

    let bestPathSum = -Infinity;
    let bestNodeId = null;

    function dfs(nodeId, parentId = null) {
        if (nodeId === null) {
            return 0;
        }

        const node = treeById.get(nodeId);
        steps.push({
            type: "enter-node",
            nodeId,
            parentId,
            nodeValue: node.value,
        });

        const rawLeftGain = node.leftId !== null ? dfs(node.leftId, nodeId) : 0;
        const rawRightGain = node.rightId !== null ? dfs(node.rightId, nodeId) : 0;
        const usedLeftGain = Math.max(0, rawLeftGain);
        const usedRightGain = Math.max(0, rawRightGain);
        const localPathSum = node.value + usedLeftGain + usedRightGain;
        const returnGain = node.value + Math.max(usedLeftGain, usedRightGain);
        const isNewBest = localPathSum > bestPathSum;

        if (isNewBest) {
            bestPathSum = localPathSum;
            bestNodeId = nodeId;
        }

        steps.push({
            type: "resolve-node",
            nodeId,
            parentId,
            nodeValue: node.value,
            rawLeftGain,
            rawRightGain,
            usedLeftGain,
            usedRightGain,
            localPathSum,
            returnGain,
            isNewBest,
            bestPathSum,
            bestNodeId,
        });

        return returnGain;
    }

    dfs(treeInput.rootId, null);

    steps.push({
        type: "complete",
        bestPathSum,
        bestNodeId,
    });

    return steps;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;

    switch (step.type) {
        case "start":
            return {
                id: `max-path-step-${stepNumber}-start`,
                stepIndex: stepNumber,
                kind: "Setup",
                title: "Initialize traversal",
                message: `The postorder DFS starts at the root node #${step.rootId}. Each node will compute a return gain and a local split-path sum.`,
            };

        case "enter-node":
            return {
                id: `max-path-step-${stepNumber}-enter-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Enter",
                title: `Enter node #${step.nodeId}`,
                message:
                    step.parentId === null
                        ? `The DFS begins at the root node #${step.nodeId} with value ${step.nodeValue}.`
                        : `The DFS descends from node #${step.parentId} into node #${step.nodeId} with value ${step.nodeValue}.`,
            };

        case "resolve-node":
            return {
                id: `max-path-step-${stepNumber}-resolve-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Resolve",
                title: step.isNewBest
                    ? `Resolve node #${step.nodeId} as new best`
                    : `Resolve node #${step.nodeId}`,
                message: step.isNewBest
                    ? `Node #${step.nodeId} uses left=${step.usedLeftGain} and right=${step.usedRightGain} with value ${step.nodeValue}, producing a new best path sum of ${step.localPathSum}. It returns ${step.returnGain} upward.`
                    : `Node #${step.nodeId} clamps child gains to left=${step.usedLeftGain} and right=${step.usedRightGain}. Its split path is ${step.localPathSum}, it returns ${step.returnGain}, and the global best stays ${step.bestPathSum}.`,
            };

        case "complete":
            return {
                id: `max-path-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: "Traversal complete",
                message: `The DFS is complete. The maximum path sum is ${step.bestPathSum}, first achieved at node #${step.bestNodeId}.`,
            };

        default:
            return {
                id: `max-path-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the maximum path sum visualization to the next state.",
            };
    }
}

export default class BinaryTreeMaximumPathSumScene extends BaseThreeScene {
    setup(props) {
        this.treeInput = normalizeTreeInput(props.input);
        this.rootId = this.treeInput.rootId;
        this.treeLayout = createBinaryTreeLayout(this.treeInput);
        this.treeById = buildTreeById(this.treeInput.nodes);
        this.laneYValues = Array.from(
            new Set(this.treeLayout.map((node) => node.position.y * GRAPH_SCALE))
        ).sort((a, b) => b - a);

        const maxAbsTreeX = Math.max(
            ...this.treeLayout.map((node) => Math.abs(node.position.x * GRAPH_SCALE)),
            1
        );
        const maxAbsTreeY = Math.max(
            ...this.treeLayout.map((node) => Math.abs(node.position.y * GRAPH_SCALE)),
            1
        );

        this.scene.background = new THREE.Color(0x050816);
        this.scene.fog = new THREE.Fog(0x050816, 11, 22);
        this.camera.position.set(0.15, 0, 7.5 + maxAbsTreeX * 0.58 + maxAbsTreeY * 0.6);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7);
        keyLight.position.set(4, 8, 10);
        this.scene.add(keyLight);

        const leftLight = new THREE.PointLight(0x60a5fa, 16, 18, 2);
        leftLight.position.set(-5.5, 2.8, 5);
        this.scene.add(leftLight);

        const rightLight = new THREE.PointLight(0xd946ef, 15, 18, 2);
        rightLight.position.set(5.5, -1.5, 5);
        this.scene.add(rightLight);

        this.graphRoot = new THREE.Group();
        this.scene.add(this.graphRoot);

        this.treeNodes = new Map();
        this.treePositions = new Map();
        this.valueLabels = new Map();
        this.idBadges = new Map();
        this.returnBadges = new Map();
        this.localSumBadges = new Map();
        this.treeEdges = new Map();

        this.backdrop = null;
        this.laneMeshes = [];
        this.presentationTime = 0;
        this.lastBackdropAspect = null;

        this.buildBackdrop();
        this.buildTree();
        this.buildIndicators();
        this.resetAnimationState();
        this.advanceStep();
    }

    getViewportSizeAtZ(worldZ) {
        const distance = Math.abs(this.camera.position.z - worldZ);
        const verticalFovRadians = THREE.MathUtils.degToRad(this.camera.fov);
        const height = 2 * Math.tan(verticalFovRadians * 0.5) * distance;
        const width = height * this.camera.aspect;

        return { width, height };
    }

    buildBackdrop() {
        this.backdrop = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.76,
            })
        );
        this.backdrop.position.set(this.camera.position.x, 0, BACKDROP_Z);
        this.backdrop.renderOrder = -20;
        this.scene.add(this.backdrop);

        const laneMaterial = new THREE.MeshBasicMaterial({
            color: 0x0f172a,
            transparent: true,
            opacity: 0.94,
        });

        this.laneMeshes = this.laneYValues.map((y) => {
            const lane = new THREE.Mesh(
                new THREE.PlaneGeometry(1, 1),
                laneMaterial.clone()
            );
            lane.position.set(this.camera.position.x, y, LANE_Z);
            lane.renderOrder = -10;
            lane.userData.baseY = y;
            this.scene.add(lane);
            return lane;
        });

        this.updateBackdropLayout();
    }

    updateBackdropLayout() {
        if (!this.backdrop) {
            return;
        }

        const backdropSize = this.getViewportSizeAtZ(BACKDROP_Z);
        this.backdrop.position.x = this.camera.position.x;
        this.backdrop.scale.set(
            backdropSize.width * 1.06,
            backdropSize.height * 1.06,
            1
        );

        const laneSize = this.getViewportSizeAtZ(LANE_Z);
        const laneWidth = laneSize.width * 1.08;

        this.laneMeshes.forEach((lane) => {
            lane.position.x = this.camera.position.x;
            lane.scale.set(laneWidth, LANE_HEIGHT, 1);
        });

        this.lastBackdropAspect = this.camera.aspect;
    }

    onResize() {
        this.updateBackdropLayout();
    }

    buildTree() {
        this.treeLayout.forEach((nodeData) => {
            const position = nodeData.position
                .clone()
                .multiplyScalar(GRAPH_SCALE)
                .add(GRAPH_OFFSET);

            this.treePositions.set(nodeData.id, position);

            const node = createGraphNode({
                id: nodeData.id,
                position,
                radius: 0.34,
                color: NODE_IDLE_COLOR.getHex(),
            });

            node.userData.baseY = position.y;
            this.treeNodes.set(nodeData.id, node);
            this.graphRoot.add(node);

            const valueLabel = createTextSprite({
                text: nodeData.value,
                width: 150,
                height: 60,
                fontSize: 24,
                textColor: "#ffffff",
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: "rgba(2, 6, 23, 0.92)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
                scale: [0.86, 0.35],
            });
            valueLabel.position.copy(position.clone());
            valueLabel.renderOrder = 25;
            this.valueLabels.set(nodeData.id, valueLabel);
            this.graphRoot.add(valueLabel);

            const idBadge = createTextSprite({
                text: `#${nodeData.id}`,
                width: 100,
                height: 54,
                fontSize: 20,
                textColor: "#bfdbfe",
                fillStyle: "rgba(15, 23, 42, 0.9)",
                strokeStyle: "rgba(255, 255, 255, 0.1)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
                scale: [0.46, 0.26],
            });
            idBadge.position.copy(position.clone().add(new THREE.Vector3(0.42, -0.36, 0.18)));
            idBadge.renderOrder = 24;
            this.idBadges.set(nodeData.id, idBadge);
            this.graphRoot.add(idBadge);

            const returnBadge = createTextSprite({
                text: "-",
                width: 126,
                height: 66,
                fontSize: 26,
                textColor: "#dbeafe",
                fillStyle: "rgba(24, 24, 27, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.14)",
                outlineColor: "rgba(0, 0, 0, 0.96)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
                scale: [0.66, 0.36],
            });
            returnBadge.position.copy(position.clone().add(new THREE.Vector3(-0.78, 0.84, 0.08)));
            returnBadge.renderOrder = 26;
            this.returnBadges.set(nodeData.id, returnBadge);
            this.graphRoot.add(returnBadge);

            const localSumBadge = createTextSprite({
                text: "-",
                width: 126,
                height: 66,
                fontSize: 26,
                textColor: "#fbcfe8",
                fillStyle: "rgba(76, 5, 25, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.14)",
                outlineColor: "rgba(0, 0, 0, 0.96)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
                scale: [0.66, 0.36],
            });
            localSumBadge.position.copy(position.clone().add(new THREE.Vector3(0.78, 0.84, 0.08)));
            localSumBadge.renderOrder = 26;
            this.localSumBadges.set(nodeData.id, localSumBadge);
            this.graphRoot.add(localSumBadge);
        });

        this.treeLayout.forEach((nodeData) => {
            const fromPosition = this.treePositions.get(nodeData.id);

            if (nodeData.leftId !== null) {
                const edge = createGraphEdge({
                    fromId: nodeData.id,
                    toId: nodeData.leftId,
                    start: fromPosition,
                    end: this.treePositions.get(nodeData.leftId),
                    color: EDGE_IDLE_COLOR.getHex(),
                });
                this.treeEdges.set(edgeKey(nodeData.id, nodeData.leftId), edge);
                this.graphRoot.add(edge);
            }

            if (nodeData.rightId !== null) {
                const edge = createGraphEdge({
                    fromId: nodeData.id,
                    toId: nodeData.rightId,
                    start: fromPosition,
                    end: this.treePositions.get(nodeData.rightId),
                    color: EDGE_IDLE_COLOR.getHex(),
                });
                this.treeEdges.set(edgeKey(nodeData.id, nodeData.rightId), edge);
                this.graphRoot.add(edge);
            }
        });
    }

    buildIndicators() {
        this.activeRing = new THREE.Mesh(
            new THREE.RingGeometry(0.42, 0.5, 48),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.92,
                side: THREE.DoubleSide,
            })
        );
        this.graphRoot.add(this.activeRing);
    }

    resetAnimationState() {
        this.steps = buildMaxPathSteps(this.treeInput);
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;

        this.activeNodeId = null;
        this.activeEdgeKey = null;
        this.callStack = [];
        this.resolvedNodeIds = new Set();
        this.returnGains = new Map();
        this.localPathSums = new Map();
        this.bestPathSum = null;
        this.bestNodeId = null;
        this.currentReturnGain = null;
        this.currentLocalPathSum = null;

        this.presentationTime = 0;
        this.resetStepLog([]);

        this.replaceUiState({
            activeNodeId: null,
            activeNodeValue: null,
            currentReturnGain: null,
            currentLocalPathSum: null,
            bestPathSum: null,
            bestNodeId: null,
            stack: [],
            returnGainEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: "-",
            })),
            localSumEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: "-",
            })),
        });

        this.syncPlaybackState();
    }

    syncUiState() {
        this.replaceUiState({
            activeNodeId: this.activeNodeId,
            activeNodeValue:
                this.activeNodeId !== null && this.treeById.has(this.activeNodeId)
                    ? this.treeById.get(this.activeNodeId).value
                    : null,
            currentReturnGain: this.currentReturnGain,
            currentLocalPathSum: this.currentLocalPathSum,
            bestPathSum: this.bestPathSum,
            bestNodeId: this.bestNodeId,
            stack: this.callStack.map((nodeId) => ({
                nodeId,
                value: this.treeById.get(nodeId).value,
            })),
            returnGainEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: this.returnGains.has(node.id)
                    ? this.returnGains.get(node.id)
                    : "-",
            })),
            localSumEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: this.localPathSums.has(node.id)
                    ? this.localPathSums.get(node.id)
                    : "-",
            })),
        });
    }

    getStepDuration(step) {
        switch (step.type) {
            case "start":
                return 1.2;
            case "enter-node":
                return 0.75;
            case "resolve-node":
                return step.isNewBest ? 1.15 : 0.9;
            case "complete":
                return 2.0;
            default:
                return 0.8;
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        switch (step.type) {
            case "start": {
                this.activeNodeId = null;
                this.activeEdgeKey = null;
                this.currentReturnGain = null;
                this.currentLocalPathSum = null;
                break;
            }

            case "enter-node": {
                this.activeNodeId = step.nodeId;
                this.activeEdgeKey =
                    step.parentId !== null ? edgeKey(step.parentId, step.nodeId) : null;
                this.currentReturnGain = null;
                this.currentLocalPathSum = null;

                if (!this.callStack.includes(step.nodeId)) {
                    this.callStack.push(step.nodeId);
                }
                break;
            }

            case "resolve-node": {
                this.activeNodeId = step.nodeId;
                this.activeEdgeKey = null;
                this.returnGains.set(step.nodeId, step.returnGain);
                this.localPathSums.set(step.nodeId, step.localPathSum);
                this.resolvedNodeIds.add(step.nodeId);
                this.currentReturnGain = step.returnGain;
                this.currentLocalPathSum = step.localPathSum;
                this.bestPathSum = step.bestPathSum;
                this.bestNodeId = step.bestNodeId;

                if (
                    this.callStack.length > 0 &&
                    this.callStack[this.callStack.length - 1] === step.nodeId
                ) {
                    this.callStack.pop();
                } else {
                    this.callStack = this.callStack.filter((nodeId) => nodeId !== step.nodeId);
                }
                break;
            }

            case "complete": {
                this.activeNodeId = null;
                this.activeEdgeKey = null;
                this.callStack = [];
                this.currentReturnGain = null;
                this.currentLocalPathSum = null;
                this.bestPathSum = step.bestPathSum;
                this.bestNodeId = step.bestNodeId;
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    update(delta) {
        if (!this.graphRoot || !this.currentStep) {
            return;
        }

        if (this.camera.aspect !== this.lastBackdropAspect) {
            this.updateBackdropLayout();
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateGraphMotion(this.presentationTime);
        this.updateNodes(this.presentationTime);
        this.updateEdges();
        this.updateValueLabels();
        this.updateIdBadges();
        this.updateReturnBadges(this.presentationTime);
        this.updateLocalSumBadges(this.presentationTime);
        this.updateActiveRing(this.presentationTime);
    }

    updateGraphMotion(elapsed) {
        this.graphRoot.rotation.y = Math.sin(elapsed * 0.18) * 0.05;
        this.graphRoot.rotation.x = Math.cos(elapsed * 0.16) * 0.018;
        this.graphRoot.position.y = Math.sin(elapsed * 0.55) * 0.05;
    }

    updateNodes(elapsed) {
        this.treeNodes.forEach((node, nodeId) => {
            const isActive = nodeId === this.activeNodeId;
            const isBestNode = nodeId === this.bestNodeId;
            const isResolved = this.resolvedNodeIds.has(nodeId);
            const returnGain = this.returnGains.has(nodeId)
                ? this.returnGains.get(nodeId)
                : null;

            let targetColor = NODE_IDLE_COLOR;
            let targetScale = 1;
            let targetEmissiveIntensity = 0.18;

            if (isResolved && returnGain !== null && returnGain < 0) {
                targetColor = NODE_NEGATIVE_RETURN_COLOR;
                targetEmissiveIntensity = 0.34;
            }

            if (isResolved && returnGain === 0) {
                targetColor = NODE_ZERO_RETURN_COLOR;
                targetEmissiveIntensity = 0.16;
            }

            if (isResolved && returnGain !== null && returnGain > 0) {
                targetColor = NODE_POSITIVE_RETURN_COLOR;
                targetEmissiveIntensity = 0.4;
            }

            if (isBestNode) {
                targetColor = NODE_BEST_COLOR;
                targetScale = 1.14;
                targetEmissiveIntensity = 0.7;
            }

            if (isActive) {
                targetColor = NODE_ACTIVE_COLOR;
                targetScale = 1.18 + Math.sin(elapsed * 4.5) * 0.03;
                targetEmissiveIntensity = 0.9;
            }

            const bob = Math.sin(elapsed * 1.6 + nodeId * 0.45) * 0.03;
            node.position.y = THREE.MathUtils.lerp(
                node.position.y,
                node.userData.baseY + bob,
                0.12
            );

            const nextScale = THREE.MathUtils.lerp(node.scale.x, targetScale, 0.14);
            node.scale.setScalar(nextScale);
            node.material.color.lerp(targetColor, 0.14);
            node.material.emissive.lerp(targetColor, 0.14);
            node.material.emissiveIntensity = THREE.MathUtils.lerp(
                node.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.14
            );
        });
    }

    updateEdges() {
        this.treeEdges.forEach((edge, nextEdgeKey) => {
            const childResolved = this.resolvedNodeIds.has(edge.userData.toId);
            const isActive = nextEdgeKey === this.activeEdgeKey;

            let targetColor = EDGE_IDLE_COLOR;
            let targetOpacity = 0.56;

            if (childResolved) {
                targetColor = EDGE_RESOLVED_COLOR;
                targetOpacity = 0.86;
            }

            if (isActive) {
                targetColor = EDGE_ACTIVE_COLOR;
                targetOpacity = 0.98;
            }

            edge.material.color.lerp(targetColor, 0.18);
            edge.material.opacity = THREE.MathUtils.lerp(
                edge.material.opacity,
                targetOpacity,
                0.18
            );
        });
    }

    updateValueLabels() {
        this.valueLabels.forEach((label, nodeId) => {
            const isActive = this.activeNodeId === nodeId;
            const node = this.treeNodes.get(nodeId);
            const value = this.treeById.get(nodeId).value;

            updateTextSprite(label, value, {
                fontSize: 24,
                textColor: isActive ? "#000000" : "#ffffff",
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: isActive
                    ? "rgba(255, 255, 255, 0.22)"
                    : "rgba(2, 6, 23, 0.92)",
                outlineWidth: isActive ? 4 : 8,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
            });

            label.position.lerp(node.position.clone(), 0.2);
        });
    }

    updateIdBadges() {
        this.idBadges.forEach((badge, nodeId) => {
            const node = this.treeNodes.get(nodeId);
            const targetPosition = node.position
                .clone()
                .add(new THREE.Vector3(0.42, -0.36, 0.18));
            badge.position.lerp(targetPosition, 0.18);
        });
    }

    updateReturnBadges(elapsed) {
        this.returnBadges.forEach((badge, nodeId) => {
            const node = this.treeNodes.get(nodeId);
            const isActive = this.activeNodeId === nodeId;
            const hasReturn = this.returnGains.has(nodeId);
            const returnGain = hasReturn ? this.returnGains.get(nodeId) : "-";
            const isBestNode = nodeId === this.bestNodeId;

            let fillStyle = "rgba(24, 24, 27, 0.96)";
            let textColor = "#cbd5e1";
            let strokeStyle = "rgba(255, 255, 255, 0.12)";
            let outlineColor = "rgba(0, 0, 0, 0.96)";

            if (hasReturn && returnGain < 0) {
                fillStyle = "rgba(127, 29, 29, 0.98)";
                textColor = "#fee2e2";
                strokeStyle = "rgba(252, 165, 165, 0.34)";
            }

            if (hasReturn && returnGain === 0) {
                fillStyle = "rgba(30, 41, 59, 0.98)";
                textColor = "#e2e8f0";
                strokeStyle = "rgba(148, 163, 184, 0.32)";
            }

            if (hasReturn && returnGain > 0) {
                fillStyle = "rgba(13, 148, 136, 0.98)";
                textColor = "#f0fdfa";
                strokeStyle = "rgba(153, 246, 228, 0.34)";
            }

            if (isBestNode) {
                fillStyle = "rgba(192, 38, 211, 0.98)";
                textColor = "#fdf4ff";
                strokeStyle = "rgba(244, 114, 182, 0.4)";
            }

            if (isActive) {
                fillStyle = "rgba(10, 10, 10, 0.98)";
                textColor = "#ffffff";
                strokeStyle = "rgba(255, 255, 255, 0.2)";
                outlineColor = "rgba(0, 0, 0, 0.98)";
            }

            updateTextSprite(badge, returnGain, {
                fontSize: 26,
                textColor,
                fillStyle,
                strokeStyle,
                outlineColor,
                outlineWidth: isActive ? 10 : 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
            });

            const pulse = isActive ? 0.08 + Math.sin(elapsed * 5.2) * 0.03 : 0;
            const targetPosition = node.position
                .clone()
                .add(new THREE.Vector3(-0.78, 0.84 + pulse, 0));
            badge.position.lerp(targetPosition, 0.18);
        });
    }

    updateLocalSumBadges(elapsed) {
        this.localSumBadges.forEach((badge, nodeId) => {
            const node = this.treeNodes.get(nodeId);
            const isActive = this.activeNodeId === nodeId;
            const hasLocal = this.localPathSums.has(nodeId);
            const localPathSum = hasLocal ? this.localPathSums.get(nodeId) : "-";
            const isBestNode = nodeId === this.bestNodeId;

            let fillStyle = "rgba(76, 5, 25, 0.96)";
            let textColor = "#fbcfe8";
            let strokeStyle = "rgba(255, 255, 255, 0.12)";
            let outlineColor = "rgba(0, 0, 0, 0.96)";

            if (hasLocal && localPathSum < 0) {
                fillStyle = "rgba(127, 29, 29, 0.98)";
                textColor = "#fee2e2";
                strokeStyle = "rgba(252, 165, 165, 0.34)";
            }

            if (hasLocal && localPathSum >= 0) {
                fillStyle = "rgba(131, 24, 67, 0.98)";
                textColor = "#fdf2f8";
                strokeStyle = "rgba(244, 114, 182, 0.34)";
            }

            if (isBestNode) {
                fillStyle = "rgba(147, 51, 234, 0.98)";
                textColor = "#faf5ff";
                strokeStyle = "rgba(216, 180, 254, 0.4)";
            }

            if (isActive) {
                fillStyle = "rgba(10, 10, 10, 0.98)";
                textColor = "#ffffff";
                strokeStyle = "rgba(255, 255, 255, 0.2)";
                outlineColor = "rgba(0, 0, 0, 0.98)";
            }

            updateTextSprite(badge, localPathSum, {
                fontSize: 26,
                textColor,
                fillStyle,
                strokeStyle,
                outlineColor,
                outlineWidth: isActive ? 10 : 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
            });

            const pulse = isActive ? 0.08 + Math.sin(elapsed * 5.2 + 0.4) * 0.03 : 0;
            const targetPosition = node.position
                .clone()
                .add(new THREE.Vector3(0.78, 0.84 + pulse, 0));
            badge.position.lerp(targetPosition, 0.18);
        });
    }

    updateActiveRing(elapsed) {
        if (this.activeNodeId === null || !this.treeNodes.has(this.activeNodeId)) {
            this.activeRing.material.opacity = THREE.MathUtils.lerp(
                this.activeRing.material.opacity,
                0,
                0.18
            );
            return;
        }

        const activeNode = this.treeNodes.get(this.activeNodeId);
        this.activeRing.position.lerp(activeNode.position.clone(), 0.18);
        this.activeRing.scale.setScalar(1 + Math.sin(elapsed * 4.5) * 0.08);
        this.activeRing.material.opacity = THREE.MathUtils.lerp(
            this.activeRing.material.opacity,
            0.9,
            0.18
        );
    }

    onDispose() {
        const disposeSpriteTexture = (sprite) => {
            if (!sprite || !sprite.material || !sprite.material.map) {
                return;
            }

            sprite.material.map.dispose();
        };

        this.valueLabels.forEach(disposeSpriteTexture);
        this.idBadges.forEach(disposeSpriteTexture);
        this.returnBadges.forEach(disposeSpriteTexture);
        this.localSumBadges.forEach(disposeSpriteTexture);
    }
}
